/**
 * The permission matrix, rendered from the same source the API enforces.
 *
 * Nothing on this page is fetched. `ROLE_PERMISSIONS` lives in
 * `@accounting/shared`, which both this bundle and the API import — so what is
 * drawn here is not a description of the rules, it is the rules. A matrix kept
 * in a wiki page drifts from the code within a sprint; this one cannot.
 *
 * Visible to every signed-in account rather than gated behind `users.manage`:
 * it is the document Finance reviews access against, it contains no data about
 * any individual, and the matrix is compiled into the client bundle regardless
 * of who is looking at it.
 */
import {
  PERMISSION_GROUPS,
  PERMISSION_META,
  ROLES,
  ROLE_LABELS,
  ROLE_PERMISSIONS,
  can,
  isReadOnly,
  permissionsInGroup,
  type Role,
} from '@accounting/shared'
import { useSession } from '../lib/session'
import { Badge, RoleBadge } from '../ui/Badge'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { PageHeader } from '../ui/PageHeader'
import { TableWrap, Td, Th } from '../ui/Table'
import { CheckIcon } from '../ui/icons'

export function Roles() {
  const { user } = useSession()

  return (
    <>
      <PageHeader
        title="Roles & permissions"
        description="What each role may do. The API and this interface read this matrix from the same shared module, so they cannot disagree."
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        {ROLES.map((role) => (
          <RoleCard key={role} role={role} isYours={user?.role === role} />
        ))}
      </div>

      <Card>
        <CardHeader
          title="Matrix"
          description="A tick means the role holds that grant. Anything unticked is refused by the API, not merely hidden."
        />

        <TableWrap>
          <thead>
            <tr>
              <Th className="min-w-72">Permission</Th>
              {ROLES.map((role) => (
                <Th key={role} className="text-center">
                  {ROLE_LABELS[role]}
                </Th>
              ))}
            </tr>
          </thead>

          {PERMISSION_GROUPS.map((group) => (
            <tbody key={group}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={1 + ROLES.length}
                  className="border-b border-ink-200 bg-brand-50 px-4 py-2 text-left text-xs font-semibold tracking-[0.12em] text-brand-800 uppercase"
                >
                  {group}
                </th>
              </tr>

              {permissionsInGroup(group).map((permission) => {
                const meta = PERMISSION_META[permission]

                return (
                  <tr key={permission}>
                    <Td>
                      <span className="block font-medium text-ink-900">{meta.label}</span>
                      <span className="block text-xs text-ink-600">{meta.description}</span>
                      <code className="mt-1 block font-mono text-[11px] text-ink-500">
                        {permission}
                      </code>
                    </Td>

                    {ROLES.map((role) => {
                      const granted = can(role, permission)

                      return (
                        <Td key={role} className="text-center">
                          {granted ? (
                            <span className="inline-flex items-center justify-center rounded-full bg-brand-50 p-1 text-brand-800 ring-1 ring-brand-200 ring-inset">
                              <CheckIcon />
                              <span className="sr-only">
                                {ROLE_LABELS[role]} may {meta.label.toLowerCase()}
                              </span>
                            </span>
                          ) : (
                            <>
                              <span aria-hidden className="text-ink-300">
                                —
                              </span>
                              <span className="sr-only">
                                {ROLE_LABELS[role]} may not {meta.label.toLowerCase()}
                              </span>
                            </>
                          )}
                        </Td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          ))}
        </TableWrap>
      </Card>

      <Card className="mt-6">
        <CardHeader title="How this is enforced" />
        <CardBody className="space-y-3 text-sm leading-relaxed text-ink-700">
          <p>
            Each endpoint declares the permission it requires, and the check runs before the handler.
            A client that ignores its own checks — or one written from scratch against the API — gets
            a 403 rather than data. The test suite asserts exactly that for every role.
          </p>
          <p>
            Two grants are scoped per record rather than per endpoint. Legal Production sees billing
            exceptions only for matters where they are the responsible attorney, matched by linking
            the account's email to the attorney roster Clio owns. Finance sees every matter.
          </p>
          <p>
            Role changes need no new sign-in: the role is read from the database on each request, so a
            change applies to the next one.
          </p>
        </CardBody>
      </Card>
    </>
  )
}

function RoleCard({ role, isYours }: { role: Role; isYours: boolean }) {
  const grants = ROLE_PERMISSIONS[role]

  return (
    <Card className={isYours ? 'ring-1 ring-brand-300' : undefined}>
      <CardBody>
        <div className="flex items-start justify-between gap-2">
          <RoleBadge role={role} />
          {isYours && <Badge tone="brand">Your role</Badge>}
        </div>

        <p className="mt-3 text-sm text-ink-700">
          {grants.length} of {Object.keys(PERMISSION_META).length} grants
          {isReadOnly(role) && ' · read-only'}
        </p>

        <p className="mt-1 text-xs leading-relaxed text-ink-600">{DESCRIPTIONS[role]}</p>
      </CardBody>
    </Card>
  )
}

/**
 * One sentence per role, in the terms the business uses. Kept beside the matrix
 * page rather than in the shared module because it describes the intent behind
 * a grant set, which is editorial — the grants themselves are the contract.
 */
const DESCRIPTIONS: Record<Role, string> = {
  finance:
    'Full financial visibility, report preparation and distribution, integrations, the audit log, and user administration.',
  legal_production:
    'Billing exceptions on their own matters, and nothing firm-wide. Deliberately excluded from financial dashboards.',
  executive:
    'Read-only access to the consolidated dashboards. Cannot edit, distribute, or administer anything.',
}
